import { useEffect } from "react";
import { v4 } from "uuid";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { WorkOrder } from "@/services/backend/rlt/work-orders/types/work-order";
import t from "@/lang/lang";
import { useRltTreatmentAddPositionMutation } from "@/services/backend/rlt/treatments/api/treatment-api";
import { Kind } from "@/services/backend/rlt/treatments/types/component";
import { SegmentKind } from "@/services/backend/rlt/treatments/types/segmentKind";
import { Button } from "@/shared/components/ui/button";
import { parseRTKQueryErrorToSonner } from "@/shared/components/errors/parse-r-t-k-query-error";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";

/**
 * AREA_KEYS are the area names, which are considered to be dedicated
 * to collecting extra work done during execution. It is an array
 * to allow for future extensions (e.g. different languages, wish to
 * change naming, ...).
 */
export const EXECUTION_AREA_KEYS = ["Zusatz bei Ausführung"];

export function AddExtraWorkDuringExecutionDropDown({
  workOrder,
}: {
  workOrder: WorkOrder;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button>
          <Plus />
          <span className="hidden xl:inline">{t("Zusatzarbeiten")}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuLabel>{t("Zusatzarbeiten")}</DropdownMenuLabel>
        <DropdownMenuGroup>
          <AddExtraButton
            workOrder={workOrder}
            componentKind={Kind.ExtraHours}
          />
          <AddExtraButton
            workOrder={workOrder}
            componentKind={Kind.AccessPanel}
          />
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function AddExtraButton({
  componentKind,
  workOrder,
}: {
  componentKind: Kind;
  workOrder: WorkOrder;
}) {
  const [post, { isLoading, error, reset }] =
    useRltTreatmentAddPositionMutation();

  useEffect(() => {
    if (error) {
      toast(...parseRTKQueryErrorToSonner(error));
      reset();
    }
  }, [error, reset]);

  const doAdd = () => {
    if (isLoading) return;

    post({
      id: workOrder.treatmentId,
      positionId: v4(),
      segment: SegmentKind.Extras,
      area: EXECUTION_AREA_KEYS[0],
      componentKind,
    });
  };

  return (
    <DropdownMenuItem onClick={doAdd} disabled={isLoading}>
      {t(componentKind)}
    </DropdownMenuItem>
  );
}
